import { $api } from "@babackapp/private-api-client/generated/client";
import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";
import { useQueryClient } from "@tanstack/react-query";

/** A shop has one progress row per collection and per run mode. */
export const runOf = (
  states: PiiBackfillStateDto[],
  mode: PiiBackfillStateDto["mode"],
): PiiBackfillStateDto[] => states.filter((state) => state.mode === mode);

const BACKFILL_PATH = "/internal/pii-vault/shops/{shopId}/backfill";
const PAUSE_PATH = "/internal/pii-vault/shops/{shopId}/backfill/pause";

const statusKey = (shopId: string) => [
  "get",
  BACKFILL_PATH,
  { params: { path: { shopId } } },
];

const POLL_MS = 3_000;

/**
 * Progress of the vault backfill for a shop. Polls only while a collection is
 * still running: a finished or paused shop moves on its own no more.
 */
export function usePiiBackfillStatus(shopId: string) {
  return $api.useQuery(
    "get",
    BACKFILL_PATH,
    { params: { path: { shopId } } },
    {
      refetchInterval: (query) =>
        query.state.data?.states.some(
          (state) => !state.isDone && !state.isPaused,
        )
          ? POLL_MS
          : false,
    },
  );
}

export function useStartPiiBackfill(shopId: string) {
  const queryClient = useQueryClient();

  return $api.useMutation("post", BACKFILL_PATH, {
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: statusKey(shopId) }),
  });
}

export function usePausePiiBackfill(shopId: string) {
  const queryClient = useQueryClient();

  return $api.useMutation("post", PAUSE_PATH, {
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: statusKey(shopId) }),
  });
}
